import classnames from 'classnames'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import MarkdownForInvitation from 'components/markdown_for_invitation'
import 'styles/components/invitation-tabs.scss'

const EVENT_BODY = 'eventBody'
const EVENT_ABOUT = 'eventAbout'

class Panel {
  constructor(type, id) {
    this.type = type
    this.id = id
  }

  static createPanels() {
    return [EVENT_BODY, EVENT_ABOUT].map((type, i) => new Panel(type, i))
  }

  toString() {
    switch (this.type) {
      case EVENT_BODY:
        return I18n.t('components.invitations_tabs.event.body')
      case EVENT_ABOUT:
        return I18n.t('components.invitations_tabs.event.about')
      default:
        return null
    }
  }
}

const TabButtons = ({ position, selected, showEventAbout, showSp, tabPanels, onClick }) => {
  const filteredPanels = tabPanels.filter(panel => {
    if (panel.type === 'eventAbout') {
      return showEventAbout
    }
    return true
  })

  if (filteredPanels.length === 1) {
    return null
  }

  const selectedTab = selected + 1 >= filteredPanels.length ? filteredPanels.length - 1 : selected
  const className = classnames('invitation-tab-bar', {
    'is-top': position && position === 'top',
    'is-bottom': position && position === 'bottom',
    'show-sp': showSp
  })

  return (
    <div className={className}>
      {filteredPanels.map((panel, i) => {
        const panelClassName = classnames('btn btn-tab', {
          'is-active': selectedTab === i
        })

        return (
          <button
            type="button"
            className={panelClassName}
            key={panel.id}
            onClick={() => onClick(i)}
            style={{ width: `${100 / filteredPanels.length}%` }}
          >
            {panel.toString()}
          </button>
        )
      })}
    </div>
  )
}

TabButtons.propTypes = {
  position: PropTypes.string,
  selected: PropTypes.number.isRequired,
  showEventAbout: PropTypes.bool.isRequired,
  showSp: PropTypes.bool,
  tabPanels: PropTypes.arrayOf(PropTypes.instanceOf(Panel)).isRequired,
  onClick: PropTypes.func.isRequired
}

TabButtons.defaultProps = {
  position: null,
  showSp: false
}

const TabPanel = ({ eventAbout, eventBody, panel }) => {
  const content = (() => {
    switch (panel.type) {
      case EVENT_BODY:
        return eventBody
      case EVENT_ABOUT:
        return eventAbout
      default:
        return null
    }
  })()

  return <MarkdownForInvitation content={content} />
}

TabPanel.propTypes = {
  eventAbout: PropTypes.string,
  eventBody: PropTypes.string,
  panel: PropTypes.instanceOf(Panel).isRequired
}

TabPanel.defaultProps = {
  eventAbout: null,
  eventBody: null
}

const AppliedContent = ({ status, url }) => {
  const className = classnames('invitation-action')
  const linkClassName = classnames('btn btn-applied', {
    'btn-disabled': isEmpty(url)
  })

  if (status) {
    return (
      <div className={className}>
        <div className="already-applied">
          <svg className="icon-check" viewBox="0 0 32 32">
            <path d="M12 21.56l-5.56-5.56-1.893 1.88 7.453 7.453 16-16-1.88-1.88z" />
          </svg>
          {I18n.t('components.invitations_tabs.message.applied')}
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      {isEmpty(url) ? (
        <span className={linkClassName}>{I18n.t('components.invitations_tabs.button.interest')}</span>
      ) : (
        <a className={linkClassName} href={url}>
          {I18n.t('components.invitations_tabs.button.interest')}
        </a>
      )}
    </div>
  )
}

AppliedContent.propTypes = {
  status: PropTypes.bool.isRequired,
  url: PropTypes.string
}

AppliedContent.defaultProps = {
  url: null
}

export default class InvitationsTabs extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      selected: props.selected
    }
    this.panels = Panel.createPanels()
  }

  static getDerivedStateFromProps(props, state) {
    const { eventAbout } = props
    const { selected } = state

    return {
      ...state,
      selected: isEmpty(eventAbout) ? 0 : selected
    }
  }

  handleClick(index) {
    this.setState({
      selected: index
    })
  }

  render() {
    const { applied, eventAbout, eventBody, name } = this.props
    const { selected } = this.state
    const tabProps = {
      eventAbout,
      eventBody,
      name,
      selected,
      showEventAbout: !isEmpty(eventAbout),
      tabPanels: this.panels,
      onClick: index => this.handleClick(index)
    }

    const filteredPanels = this.panels.filter(panel => {
      if (panel.type === EVENT_ABOUT) {
        return eventAbout && eventAbout.length > 0
      }
      return true
    })

    const selectedTab = selected + 1 >= filteredPanels.length ? filteredPanels.length - 1 : selected

    return (
      <div className="invitation-tabs">
        <TabButtons {...tabProps} position="top" />
        <div className="invitation-tab-panels">
          {filteredPanels.map((panel, i) => {
            const className = classnames('invitation-tab-panel', {
              'is-active': selectedTab === i
            })

            return (
              <div className={className} key={panel.id}>
                <div className="invitation-body">
                  {panel.type !== EVENT_BODY || isEmpty(name) ? null : (
                    <div className="name">
                      {name}
                      <span>{I18n.t('common.honorifics')}</span>
                    </div>
                  )}
                  <TabPanel eventAbout={eventAbout} eventBody={eventBody} panel={panel} />
                </div>
              </div>
            )
          })}
        </div>
        <AppliedContent status={applied && applied.status} url={applied && applied.url} />
        <TabButtons {...tabProps} position="bottom" showSp />
      </div>
    )
  }
}

InvitationsTabs.propTypes = {
  applied: PropTypes.shape({
    status: PropTypes.bool.isRequired,
    url: PropTypes.string
  }).isRequired,
  eventAbout: PropTypes.string,
  eventBody: PropTypes.string,
  name: PropTypes.string.isRequired,
  selected: PropTypes.number
}

InvitationsTabs.defaultProps = {
  eventAbout: null,
  eventBody: null,
  selected: 0
}
